// #1399 — Kopfzeile über der Inbox-Liste: Kästchen „Alle auswählen“ und,
// sobald etwas gewählt ist, „N ausgewählt · Erledigt · Zurückstellen …“.
// Wunsch aus dem Team: Strg+A gab es nur in der Mac-App und war versteckt.

import { useEffect, useRef, useState } from "preact/hooks";
import { CheckCheck, Clock, MailOpen } from "lucide-preact";
import { SnoozePicker } from "./SnoozePicker";
import type { InboxItem, InboxItemState } from "../../lib/api";
import {
  bulkActionsFor,
  bulkTargets,
  inboxKey,
  selectAllState,
  toggleAll,
  type InboxBulkAction,
} from "../../lib/inboxSelection";

export interface InboxBulkBarProps {
  /** Die gerade SICHTBAREN Einträge (nach Reiter und Ausblenden eigener Aktionen). */
  visible: InboxItem[];
  tab: InboxItemState;
  selected: ReadonlySet<string>;
  onSelectionChange: (next: Set<string>) => void;
  onBulk: (items: InboxItem[], action: InboxBulkAction, snoozeUntil?: string) => Promise<void>;
}

const actBtn =
  "inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-[color:var(--color-on-surface)] hover:text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors disabled:opacity-50";

export function InboxBulkBar({ visible, tab, selected, onSelectionChange, onBulk }: InboxBulkBarProps): preact.JSX.Element | null {
  const boxRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [showSnooze, setShowSnooze] = useState(false);
  const state = selectAllState(visible, selected);
  const chosen = visible.filter((i) => selected.has(inboxKey(i)));

  // „teilweise“ gibt es nur als DOM-Eigenschaft, nicht als Attribut.
  useEffect(() => {
    if (boxRef.current) boxRef.current.indeterminate = state === "some";
  }, [state]);

  if (visible.length === 0) return null;

  const run = async (action: InboxBulkAction, until?: string): Promise<void> => {
    const targets = bulkTargets(chosen, action);
    if (busy || targets.length === 0) return;
    setBusy(true);
    try {
      await onBulk(targets, action, until);
    } finally {
      setBusy(false);
    }
  };

  const actions = bulkActionsFor(tab);

  return (
    <div class="mb-2 flex min-h-9 flex-wrap items-center gap-x-3 gap-y-1 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-4 py-1.5">
      <label class="flex cursor-pointer items-center gap-2 text-xs text-[color:var(--color-on-surface-variant)]">
        <input
          ref={boxRef}
          type="checkbox"
          class="size-4 accent-[color:var(--color-primary)]"
          checked={state === "all"}
          onChange={() => onSelectionChange(toggleAll(visible, selected))}
          aria-label="Alle auswählen"
        />
        {chosen.length > 0 ? (
          <span class="font-medium text-[color:var(--color-on-surface)]">
            {chosen.length} ausgewählt
          </span>
        ) : (
          <span>Alle auswählen</span>
        )}
      </label>

      {chosen.length > 0 && (
        <div class="ml-auto flex flex-wrap items-center gap-1" aria-label="Aktionen für die Auswahl">
          {actions.includes("done") && (
            <button type="button" class={actBtn} disabled={busy} onClick={() => void run("done")}>
              <CheckCheck size={14} aria-hidden="true" />
              Erledigt
            </button>
          )}
          {actions.includes("unread") && (
            <button
              type="button"
              class={actBtn}
              disabled={busy || bulkTargets(chosen, "unread").length === 0}
              onClick={() => void run("unread")}
            >
              <MailOpen size={14} aria-hidden="true" />
              Ungelesen
            </button>
          )}
          {actions.includes("snooze") && (
            <div class="relative">
              <button
                type="button"
                class={actBtn}
                disabled={busy}
                onClick={() => setShowSnooze(!showSnooze)}
                aria-label="Auswahl zurückstellen"
              >
                <Clock size={14} aria-hidden="true" />
                Zurückstellen
              </button>
              {showSnooze && (
                <SnoozePicker
                  onPick={(until) => {
                    setShowSnooze(false);
                    void run("snooze", until);
                  }}
                  onClose={() => setShowSnooze(false)}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Kästchen in der Zeile — gleiche Optik in beiden Listen-Varianten. */
export function InboxRowCheckbox({
  checked,
  title,
  onToggle,
}: {
  checked: boolean;
  title: string;
  onToggle: () => void;
}): preact.JSX.Element {
  return (
    <input
      type="checkbox"
      class="mt-0.5 size-4 shrink-0 cursor-pointer accent-[color:var(--color-primary)]"
      checked={checked}
      onChange={onToggle}
      aria-label={`Auswählen: ${title}`}
    />
  );
}
